/* One thin layer over Chart.js, and the only place in the app a Chart.js config appears.
 *
 * Conventions that hold for every chart:
 *   - colours come from the `--chart-*` custom properties, never hard-coded;
 *   - ordered measures use the ordinal ramp; nominal categories get one colour;
 *   - two measures of different units get two charts, never two y-scales;
 *   - values are direct-labelled, so nothing depends on telling two hues apart.
 */

import { Chart, type Plugin } from 'chart.js/auto';

export const cssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** Categorical slots: one hue per series, never ordered. */
export const series = (): string[] => [
  cssVar('--chart-series-1'),
  cssVar('--chart-series-2'),
  cssVar('--chart-series-3'),
  cssVar('--chart-series-4'),
];

/** Ordinal ramp, dark to light, for measures that have an order. */
export const ordinal = (): string[] => [
  cssVar('--chart-ordinal-1'),
  cssVar('--chart-ordinal-2'),
  cssVar('--chart-ordinal-3'),
  cssVar('--chart-ordinal-4'),
];

/** Direct value labels, so a chart can be read without matching hues. `mode: 'end'` labels
 *  the last point of each line only; anything else labels every bar. */
interface DirectLabelOptions {
  format?: (value: number) => string;
  label?: (value: number, index: number, datasetIndex: number) => string;
  mode?: 'end';
}

const directLabels: Plugin = {
  id: 'directLabels',
  afterDatasetsDraw(chart, _args, opts: DirectLabelOptions) {
    // Same opt-in rule as the crosshair below: no formatter declared, no labels drawn.
    // A registered plugin runs on every chart, and Chart.js passes `{}` rather than
    // nothing when a chart's options never mention it.
    if (!opts?.label && !opts?.format) return;
    const { ctx } = chart;
    const label =
      opts.label ?? ((v: number) => (opts.format ? opts.format(v) : String(v)));

    ctx.save();
    ctx.font = `10px ${Chart.defaults.font.family}`;
    ctx.fillStyle = cssVar('--text-secondary');
    ctx.textBaseline = 'middle';

    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      const values = ds.data as (number | null)[];

      if (opts.mode === 'end') {
        let last = -1;
        values.forEach((v, i) => {
          if (v != null) last = i;
        });
        if (last < 0) return;
        const element = meta.data[last];
        ctx.textAlign = 'right';
        ctx.fillText(label(values[last] as number, last, di), element.x - 6, element.y - 14);
        return;
      }

      meta.data.forEach((element, i) => {
        if (values[i] == null) return;
        ctx.textAlign = 'left';
        ctx.fillText(label(values[i] as number, i, di), element.x + 8, element.y);
      });
    });

    ctx.restore();
  },
};

/** Chart.js draws no crosshair, and without one a tooltip on a shared index points at
 *  nothing in particular. */
const crosshair: Plugin = {
  id: 'crosshair',
  afterDatasetsDraw(chart, _args, opts: { enabled?: boolean }) {
    if (!opts?.enabled) return;
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = cssVar('--chart-grid');
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(active[0].element.x, chartArea.top);
    ctx.lineTo(active[0].element.x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

let registered = false;

/** Registration and defaults, applied once. Called from the app root rather than at import
 *  time, so the font tokens resolve. */
export function setUpCharts(): void {
  if (registered) return;
  registered = true;
  Chart.register(directLabels, crosshair);
  Chart.defaults.font.family = cssVar('--chart-font') || 'system-ui, sans-serif';
  Chart.defaults.font.size = 10;
  Chart.defaults.color = cssVar('--text-muted');
  // Long enough to read as movement, short enough not to delay a filter change.
  Chart.defaults.animation = { duration: 260 };
}

/** Category axis shared by all three bar shapes: no grid, no border, muted 10px labels. */
export const categoryAxis = (maxChars = 34) => ({
  grid: { display: false },
  border: { display: false },
  ticks: {
    color: cssVar('--text-muted'),
    autoSkip: false,
    callback(this: { getLabelForValue: (i: number) => string }, value: string | number) {
      const label = String(this.getLabelForValue(Number(value)));
      return label.length > maxChars ? `…${label.slice(-(maxChars - 1))}` : label;
    },
  },
});

/** Value axis for the bar shapes: hidden, because every bar is direct-labelled. */
export const hiddenValueAxis = () => ({ display: false, beginAtZero: true, grace: '12%' });

export { Chart };
